import axios from "axios";
import { Message } from "element-ui";
import store from "@/store";

// 从环境变量获取API基础URL
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

/**
 * 从 URL 获取指定参数值
 */
function getUrlParam(name) {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(name);
}

/**
 * 存储 workspaceId
 */
export function getWorkspaceId() {
  const ws = getUrlParam("ws");
  if (ws) {
    localStorage.setItem("workspaceId", ws);
    return ws;
  }
  return localStorage.getItem("workspaceId") || "";
}

/**
 * 一次性登录逻辑
 */
export async function oneTimeLoginIfNeeded() {
  const loginCode = getUrlParam("loginCode") || "";
  if (!loginCode) return;

  const email = getUrlParam("email") || "";
  const ws = getUrlParam("ws") || "";

  if (email) {
    // 确保 workspaceId 被存储
    getWorkspaceId();
    try {
      const response = await axios.post(
        `${API_BASE_URL}/v1/client-portal/auth/quick-login`,
        {
          email,
          token: loginCode
        },
        {
          headers: {
            Accept: "application/json",
            "X-Workspace-Id": ws,
          },
        }
      );

      const token = response?.data?.body?.access_token || response?.body?.access_token;
      if (token) {
        localStorage.setItem("token", token);
        window.location.href = "/user";
      } else {
        console.error("一次性登录失败：未返回 token。");
      }
    } catch (error) {
      console.error("一次性登录错误:", error);
    }
  }
}

// 创建axios实例
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json"
  }
});

// Token 刷新相关变量
let isRefreshing = false;
let refreshSubscribers = [];

function onRefreshed(newToken) {
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
}

// 强制跳转到登录页
function redirectToLogin() {
  localStorage.removeItem("token");
  Message.error("Session expired, please log in again.");
  setTimeout(() => {
    window.location.href = "/sign_in";
  }, 1000);
}

// 请求拦截器
apiClient.interceptors.request.use(
  (config) => {
    store.dispatch("loading/showLoading");

    const token = localStorage.getItem("token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    const workspaceId = localStorage.getItem("workspaceId");
    if (workspaceId) {
      config.headers["X-Workspace-Id"] = workspaceId;
    }

    return config;
  },
  (error) => {
    store.dispatch("loading/hideLoading");
    return Promise.reject(error)
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => {
    store.dispatch("loading/hideLoading");
    if (response.status === 204) {
      return { success: true };
    }
    return response.data;
  },
  async (error) => {
    store.dispatch("loading/hideLoading");
    const { response, config } = error;
    if (!config) {
      return Promise.reject(error);
    }

    // 避免 refresh 请求进入死循环
    if (config.url.includes("sign_in")) {
      redirectToLogin();
      return Promise.reject(error);
    }

    // 处理 401（未授权）错误
    if (response?.status === 401) {
      console.log("Token 过期，尝试刷新...");
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshResponse = await axios.put(
            `${API_BASE_URL}/v1/client-portal/auth/login-refresh`,
            {},
            { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
          );
          const newToken = refreshResponse.body?.access_token;
          if (newToken) {
            localStorage.setItem("token", newToken);
            isRefreshing = false;
            onRefreshed(newToken);

            // 重试原请求
            return apiClient({
              ...config,
              headers: { ...config.headers, Authorization: `Bearer ${newToken}` }
            });
          }
        } catch (refreshError) {
          console.error("刷新 Token 失败", refreshError);
          isRefreshing = false;
          redirectToLogin();
          return Promise.reject(refreshError);
        }
      }
      // 如果已经在刷新 token，则等待刷新完成后再执行原请求
      return new Promise((resolve) => {
        refreshSubscribers.push((newToken) => {
          resolve(
            apiClient({
              ...config,
              headers: { ...config.headers, Authorization: `Bearer ${newToken}` }
            })
          );
        });
      });
    }

    // 其他错误处理
    const errorMessage =
      response?.data?.body?.message ||
      response?.data?.message ||
      error.message ||
      "请求出错";
    console.log("Error Message:", errorMessage);
    Message.error(errorMessage);

    return Promise.reject(error);
  }
);

export default apiClient;